// ../analyzeSolanaTransaction/createTokenTransfers.ts からコピー

import type { TokenAccountMap } from '../analyzeSolanaTransaction/types'
import type { TransactionDetail } from '../schemas/transactionDetail'
import type {
  NativeTokenTransfer,
  SplTokenTransfer,
  TokenTransfer,
} from '../schemas/transfer'
import type { SplTokenInfo } from '../splTokenDatasource'
import { parseInstructionToTransfer } from './parseInstructionToTransfer'

type CreateTokenTransfersParameters = {
  readonly transaction: TransactionDetail
  readonly tokenAccounts: TokenAccountMap
  readonly splTokens: readonly SplTokenInfo[]
}

export const createTokenTransfers = ({
  transaction,
  tokenAccounts,
  splTokens,
}: CreateTokenTransfersParameters): {
  solTransfers: NativeTokenTransfer[]
  tokenTransfersFromBalances: SplTokenTransfer[]
  tokenTransfersFromInstructions: TokenTransfer[]
} => {
  const solTransfers: NativeTokenTransfer[] = []
  const tokenTransfersFromBalances: SplTokenTransfer[] = []
  const tokenTransfersFromInstructions: TokenTransfer[] = []

  // create transfer by instruction
  const innerInstructions = transaction.meta?.innerInstructions ?? []
  for (const [
    index,
    instruction,
  ] of transaction.transaction.message.instructions.entries()) {
    const transfer = parseInstructionToTransfer({
      index,
      innerIndex: undefined,
      instruction,
      transaction,
      tokenAccounts,
      splTokens,
    })
    if (transfer !== undefined) {
      tokenTransfersFromInstructions.push(transfer)
    }
    const innerInstruction = innerInstructions.find(
      (innerInstruction) => innerInstruction.index === index,
    )
    if (innerInstruction !== undefined) {
      for (const [
        innerIndex,
        instruction,
      ] of innerInstruction.instructions.entries()) {
        const transfer = parseInstructionToTransfer({
          index: innerInstruction.index,
          innerIndex,
          instruction,
          transaction,
          tokenAccounts,
          splTokens,
        })
        if (transfer !== undefined) {
          tokenTransfersFromInstructions.push(transfer)
        }
      }
    }
  }

  // create transfer by diff between postBalance and preBalance
  if (transaction.meta !== null) {
    for (const [
      index,
      accountKey,
    ] of transaction.transaction.message.accountKeys.entries()) {
      const address = accountKey.pubkey
      const diff =
        BigInt(transaction.meta.postBalances[index]) -
        BigInt(transaction.meta.preBalances[index])
      if (diff > 0) {
        solTransfers.push({
          type: 'native',
          instructionIndex: undefined,
          innerInstructionIndex: undefined,
          from: undefined,
          to: address,
          amount: diff,
        })
      } else if (diff < 0) {
        solTransfers.push({
          type: 'native',
          instructionIndex: undefined,
          innerInstructionIndex: undefined,
          from: address,
          to: undefined,
          amount: -1n * diff,
        })
      }
    }
  }

  // create transfer by diff between postTokenBalance and preTokenBalance
  const preTokenBalances = transaction.meta?.preTokenBalances ?? []
  const postTokenBalances = transaction.meta?.postTokenBalances ?? []
  const mintAddresses = new Set([
    ...preTokenBalances.map((tokenBalance) => tokenBalance.mint),
    ...postTokenBalances.map((tokenBalance) => tokenBalance.mint),
  ])
  for (const [
    index,
    accountKey,
  ] of transaction.transaction.message.accountKeys.entries()) {
    const address = accountKey.pubkey
    for (const mint of mintAddresses) {
      const preBalance = preTokenBalances.find(
        (tokenBalance) =>
          tokenBalance.accountIndex === index && tokenBalance.mint === mint,
      )?.uiTokenAmount.amount
      const postBalance = postTokenBalances.find(
        (tokenBalance) =>
          tokenBalance.accountIndex === index && tokenBalance.mint === mint,
      )?.uiTokenAmount.amount
      if (preBalance === undefined && postBalance === undefined) {
        continue
      }
      const preBalanceBn = BigInt(preBalance ?? '0')
      const postBalanceBn = BigInt(postBalance ?? '0')
      const diff = postBalanceBn - preBalanceBn
      if (diff === 0n) {
        continue
      }
      const splTokenInfo = splTokens.find((token) => token.address === mint)
      const tokenInfo =
        splTokenInfo && splTokenInfo.asset.type === 'CryptoCurrency'
          ? {
              address: mint,
              name: splTokenInfo.asset.name,
              symbol: splTokenInfo.asset.symbol,
              decimals: splTokenInfo.decimals,
              currency: splTokenInfo.asset,
            }
          : {
              address: mint,
              name: '',
              symbol: '',
              decimals: 0,
              currency: {
                symbol: 'UNKNOWN',
                type: 'CryptoCurrency' as const,
                id: 'UNKNOWN',
                name: 'Unknown Token',
                updatedAt:
                  process.env.NODE_ENV === 'test' ? 1672531200000 : Date.now(),
              },
            }

      if (diff > 0) {
        // address here is the token account address from accountKeys
        const toOwner = tokenAccounts[address]?.owner ?? address
        tokenTransfersFromBalances.push({
          type: 'spl',
          instructionIndex: undefined,
          innerInstructionIndex: undefined,
          token: tokenInfo,
          from: undefined,
          to: toOwner,
          amount: diff,
        })
      } else if (diff < 0) {
        // address here is the token account address from accountKeys
        const fromOwner = tokenAccounts[address]?.owner ?? address
        tokenTransfersFromBalances.push({
          type: 'spl',
          instructionIndex: undefined,
          innerInstructionIndex: undefined,
          token: tokenInfo,
          from: fromOwner,
          to: undefined,
          amount: -1n * diff,
        })
      }
    }
  }

  return {
    solTransfers,
    tokenTransfersFromBalances,
    tokenTransfersFromInstructions,
  }
}
